import React, { forwardRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OptimizedImage } from '../optimized-image/optimized-image';
import { audioManager } from 'lib/audio/audio-manager';
import { AUDIO_TYPES } from 'lib/audio/audio.types';

import blick from 'assets/animation/buttons/blick.png?format=webp;avif&as=picture';
import glare from 'assets/animation/buttons/glare-secondary.png?format=webp;avif&as=picture';
import LoadingSpinner from 'assets/animation/buttons/spinner-brown.svg?react';

import styles from './SecondaryBtn.module.css';

interface ISecondaryBtnProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  text?: string;
  svg?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  childClassName?: string;
  isLoading?: boolean;
  showGlare?: boolean;
}

const SecondaryBtn = forwardRef<HTMLButtonElement, ISecondaryBtnProps>((props, ref) => {
  const [isHovered, setIsHovered] = useState(false);

  const { className, childClassName, text, svg, onClick, disabled, isLoading, showGlare, ...rest } =
    props;
  const { t } = useTranslation();

  const handleClick = useCallback(() => {
    audioManager.playSound(AUDIO_TYPES.CLICK);
    onClick();
  }, [onClick]);

  return (
    <button
      ref={ref}
      className={`${styles.buttonSecondary} ${className ? className : ''} ${
        disabled ? 'bg-[#322E66]' : 'bg-secondary-border-new'
      }`}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={disabled}
      {...rest}
    >
      <div
        className={`group ${styles.buttonSecondaryInner} ${
          disabled
            ? 'bg-[#100E34] text-[#322E66] shadow-primary-btn-disabled'
            : 'bg-secondary-btn text-[#FFECC0] shadow-secondary-btn transition-all duration-300 ease-in-out hover:bg-secondary-btn-hover active:shadow-secondary-btn-active'
        } ${childClassName ? childClassName! : ''}`}
      >
        <div className={`${disabled ? '' : 'group-active:scale-90'}`}>
          {isLoading ? (
            <div className="flex items-center justify-center gap-1">
              <LoadingSpinner className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <>
              {!disabled ? (svg ?? '') : ''}
              {t(text ?? '')}
            </>
          )}
        </div>

        {!disabled && showGlare && (
          <>
            <OptimizedImage
              imageData={blick}
              alt="wide rectangle"
              className="absolute left-[-10%] top-[-30px] z-30 max-h-[136px] max-w-[144px] transition-all duration-300 ease-in-out group-hover:left-custom-left"
            />
            <OptimizedImage
              imageData={glare}
              alt="glare effect"
              className={`absolute left-[-100%] top-[-40px] z-30 h-[130px] w-[96px] transition-all duration-500 ease-in-out group-hover:left-[100%] ${
                !isHovered ? 'animate-glare' : ''
              }`}
            />
          </>
        )}
      </div>
    </button>
  );
});

export default SecondaryBtn;
